import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UnifiedUserAvatar } from '@/components/UserAvatar/unified';
import { ROUTER_INDEX } from '@/entry/route';
import logo from '@/assets/canvopix_logo.png';
import { CanvopixTheme } from '../../types';
import { IconTheme } from '../Icons';
import { Theme } from '@shared/theme';

interface HeaderProps {
  headerHeight: number;
  theme: Theme;
  onToggleTheme: () => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
  onSearchSubmit: (e: React.FormEvent) => void;
}

const themeLabelMap: Record<CanvopixTheme, string> = {
  light: 'Light',
  dark: 'Dark',
  radiant: 'Radiant',
};

export function Header({
  headerHeight,
  theme,
  onToggleTheme,
  searchValue,
  setSearchValue,
  onSearchSubmit,
}: HeaderProps) {
  const { t } = useTranslation('app');
  const navigate = useNavigate();

  return (
    <header
      className="fixed inset-x-0 top-0 z-40 border-b border-[var(--cp-border)] bg-[var(--cp-nav-bg)] text-[var(--cp-fg)] backdrop-blur"
      style={{ height: headerHeight }}
    >
      <div className="flex h-full w-full items-center px-3 sm:px-4">
        <div className="flex flex-1 items-center justify-start">
          <button
            type="button"
            className="flex items-center gap-2 rounded-md px-2 py-1 text-sm font-semibold hover:bg-[var(--cp-secondary-bg-hover)]"
            onClick={() => navigate(ROUTER_INDEX)}
          >
            <img src={logo} alt="Canvopix" className="h-8 w-8 rounded object-cover" />
            <span className="hidden sm:inline">Canvopix</span>
          </button>
        </div>

        <form onSubmit={onSearchSubmit} className="flex w-full max-w-xl items-center justify-center px-2">
          <div className="relative w-full">
            <input
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder={t('layout.header.search_placeholder')}
              className="w-full rounded-lg border border-[var(--cp-border)] bg-[var(--cp-card-bg)] px-3 py-2 text-sm text-[var(--cp-fg)] placeholder:text-[var(--cp-muted-2)] focus:outline-none focus:ring-2 focus:ring-[var(--cp-selection-bg)]"
            />
          </div>
        </form>

        <div className="flex flex-1 items-center justify-end gap-2">
          <button
            type="button"
            onClick={onToggleTheme}
            className="flex items-center gap-2 rounded-lg border border-[var(--cp-secondary-border)] bg-[var(--cp-secondary-bg)] px-2.5 py-2 text-sm font-semibold text-[var(--cp-fg)] hover:bg-[var(--cp-secondary-bg-hover)]"
          >
            <IconTheme theme={theme} />
            <span className="hidden sm:inline">{themeLabelMap[theme]}</span>
          </button>
          <UnifiedUserAvatar />
        </div>
      </div>
    </header>
  );
}
